import { shortName, compactName } from '../../../utils/playerName';
import type { CompetitionSlotSource, CompetitionTeam } from '../../../types';

/**
 * Localized knockout round name, by how many rounds remain (0 = final).
 * Mirrors `roundLabel` in BracketView, plus an `R32` rung for the largest draws
 * (up to 64 players / 32 teams).
 */
export function roundLabel(round: number, totalRounds: number): string {
  const fromEnd = totalRounds - round;
  if (fromEnd === 0) return 'Final';
  if (fromEnd === 1) return 'Semifinais';
  if (fromEnd === 2) return 'Quartas';
  if (fromEnd === 3) return 'Oitavas';
  if (fromEnd === 4) return 'R32';
  return `Rodada ${round}`;
}

/** Team name "First Last & First Last" (full first + last, drops middle names). */
export function fullTeamName(team: CompetitionTeam | null): string {
  if (!team) return '—';
  return team.players.map((p) => shortName(p.name) || '?').join(' & ');
}

/** Tighter "First L. & First L." used in the dense group match rows. */
export function compactTeamName(team: CompetitionTeam | null): string {
  if (!team) return '—';
  return team.players.map((p) => compactName(p.name) || '?').join(' & ');
}

/**
 * Origin label printed *below* a blank bracket write-line: where the slot's
 * dupla comes from. Prefers the placeholder source ("1º Grupo 1", "Vencedor do
 * Jogo 3", "BYE") since on a blank sheet the name is filled in by pen; falls
 * back to a resolved team name, then "A definir".
 */
export function slotOriginLabel(
  source: CompetitionSlotSource | null | undefined,
  team: CompetitionTeam | null
): string {
  if (source) {
    switch (source.kind) {
      case 'group':
        return `${source.rank}º Grupo ${source.groupNumber}`;
      case 'winner':
        return `Vencedor do Jogo ${source.gameNumber}`;
      case 'loser':
        return `Perdedor do Jogo ${source.gameNumber}`;
      case 'bye':
        return 'BYE';
    }
  }
  if (team) return fullTeamName(team);
  return 'A definir';
}
